import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Link,
  TextField,
  Grid,
  Collapse,
  MenuItem,
  Badge,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CheckCircle,
  Error,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { AccountTransaction, TransactionSearchParams } from '../types/account';
import { accountService } from '../services/api';
import { formatCurrency, formatDateTime, exportToCSV } from '../utils/accountUtils';

// Option lists mirror the backend enums (TransactionType / TransactionStatus) and the
// configured currencies. Kept local so the panel stays in sync without an extra API call.
const TRANSACTION_TYPES = [
  'VOUCHER_CREATION', 'VOUCHER_PAYMENT', 'VOUCHER_CANCELLATION', 'VOUCHER_REFUND',
  'COMMISSION_CHARGE', 'COMMISSION_PAYMENT', 'SETTLEMENT_PAYMENT', 'WALLET_FUNDING',
  'WALLET_WITHDRAWAL', 'FEE_CHARGE', 'ADJUSTMENT_CREDIT', 'ADJUSTMENT_DEBIT',
  'FLOAT_LOAD', 'REVERSAL',
];
const TRANSACTION_STATUSES = [
  'PENDING', 'PROCESSING', 'POSTED', 'COMPLETED', 'FAILED', 'REVERSED', 'CANCELLED',
];
const CURRENCIES = ['USD', 'ZWG', 'ZAR'];

const humanize = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

interface TransactionFilters {
  search: string;
  type: string;
  status: string;
  currency: string;
  startDate: Date | null;
  endDate: Date | null;
  minAmount: string;
  maxAmount: string;
}

const EMPTY_FILTERS: TransactionFilters = {
  search: '',
  type: '',
  status: '',
  currency: '',
  startDate: null,
  endDate: null,
  minAmount: '',
  maxAmount: '',
};

const TransactionManagement: React.FC = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<AccountTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalElements, setTotalElements] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // `filters` holds the in-progress form; `appliedFilters` is what the table is actually showing.
  // Editing fields no longer triggers a fetch until "Apply" is pressed.
  const [filters, setFilters] = useState<TransactionFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<TransactionFilters>(EMPTY_FILTERS);

  const buildParams = (f: TransactionFilters, currentPage: number): TransactionSearchParams => ({
    search: f.search.trim() || undefined,
    type: f.type || undefined,
    status: f.status || undefined,
    currency: f.currency || undefined,
    startDate: f.startDate ? format(f.startDate, 'yyyy-MM-dd') : undefined,
    endDate: f.endDate ? format(f.endDate, 'yyyy-MM-dd') : undefined,
    minAmount: f.minAmount !== '' ? Number(f.minAmount) : undefined,
    maxAmount: f.maxAmount !== '' ? Number(f.maxAmount) : undefined,
    page: currentPage,
    size: rowsPerPage,
  });

  const loadTransactions = async (f: TransactionFilters, currentPage: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await accountService.getTransactions(buildParams(f, currentPage));
      setTransactions(response.content);
      setTotalElements(response.totalElements);
    } catch (err: any) {
      console.error('Error loading transactions:', err);
      setError(err.response?.data?.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch on pagination changes using the currently-applied filters.
  useEffect(() => {
    loadTransactions(appliedFilters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setPage(0);
    loadTransactions(filters, 0);
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(0);
    loadTransactions(EMPTY_FILTERS, 0);
  };

  const handleFilterChange = (field: keyof TransactionFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const activeFilterCount = Object.entries(appliedFilters).filter(([, v]) => v !== '' && v !== null).length;

  const handleExport = () => {
    const exportData = transactions.map((txn) => ({
      'Transaction Reference': txn.transactionRef || txn.transactionReference,
      'Transaction Date': formatDateTime(txn.transactionDate),
      Type: txn.transactionType ? humanize(txn.transactionType) : 'N/A',
      Description: txn.description,
      Currency: txn.currency,
      'Total Amount': txn.totalAmount,
      Status: txn.transactionStatus || txn.status,
      Balanced: txn.isBalanced ? 'Yes' : 'No',
      'Number of Postings': txn.numberOfPostings,
      'Voucher Number': txn.voucherNumber || 'N/A',
    }));
    exportToCSV(exportData, `transactions-${new Date().toISOString().split('T')[0]}.csv`);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Transaction Management</Typography>
        <Box display="flex" gap={1}>
          <Badge badgeContent={activeFilterCount} color="primary">
            <Button
              variant={showFilters ? 'contained' : 'outlined'}
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters((prev) => !prev)}
            >
              Filters
            </Button>
          </Badge>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport}>
            Export
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => loadTransactions(appliedFilters, page)}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Quick search row - always visible */}
      <Paper sx={{ mb: 2, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={9}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by transaction reference or voucher number..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleApplyFilters()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button fullWidth variant="contained" onClick={handleApplyFilters}>
              Search
            </Button>
          </Grid>
        </Grid>

        {/* Collapsible comprehensive filter panel */}
        <Collapse in={showFilters}>
          <Divider sx={{ my: 2 }} />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Transaction Type"
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {TRANSACTION_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>{humanize(t)}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Status"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {TRANSACTION_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>{humanize(s)}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Currency"
                  value={filters.currency}
                  onChange={(e) => handleFilterChange('currency', e.target.value)}
                >
                  <MenuItem value="">All Currencies</MenuItem>
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="From Date"
                  value={filters.startDate}
                  onChange={(date) => handleFilterChange('startDate', date)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="To Date"
                  value={filters.endDate}
                  onChange={(date) => handleFilterChange('endDate', date)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Min Amount"
                  value={filters.minAmount}
                  onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Max Amount"
                  value={filters.maxAmount}
                  onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" gap={1} justifyContent="flex-end">
                  <Button startIcon={<ClearIcon />} onClick={handleClearFilters}>
                    Clear
                  </Button>
                  <Button variant="contained" startIcon={<FilterListIcon />} onClick={handleApplyFilters}>
                    Apply Filters
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Collapse>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Transaction Ref</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Currency</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Voucher</TableCell>
                <TableCell>Postings</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center"><CircularProgress /></TableCell>
                </TableRow>
              ) : transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography color="text.secondary">No transactions found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((txn) => (
                  <TableRow key={txn.id} hover onClick={() => navigate(`/transactions/${txn.id}`)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Link component="button" variant="body2">
                        {txn.transactionRef || txn.transactionReference}
                      </Link>
                    </TableCell>
                    <TableCell>{formatDateTime(txn.transactionDate)}</TableCell>
                    <TableCell>{txn.transactionType ? humanize(txn.transactionType) : '-'}</TableCell>
                    <TableCell>{txn.description}</TableCell>
                    <TableCell><Chip label={txn.currency} size="small" variant="outlined" /></TableCell>
                    <TableCell align="right">{formatCurrency(txn.totalAmount, txn.currency)}</TableCell>
                    <TableCell>
                      {txn.voucherNumber && (
                        <Link component="button" variant="body2" onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/voucher/${txn.voucherNumber}`);
                        }}>
                          {txn.voucherNumber}
                        </Link>
                      )}
                    </TableCell>
                    <TableCell>{txn.numberOfPostings}</TableCell>
                    <TableCell>
                      <Chip
                        icon={txn.isBalanced ? <CheckCircle /> : <Error />}
                        label={txn.isBalanced ? 'Balanced' : 'Unbalanced'}
                        color={txn.isBalanced ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/transactions/${txn.id}`); }}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default TransactionManagement;
